// @flow

import { action, observable, runInAction } from "mobx"
import FleetGateway from "shared/apis/FleetGateway"
import RcGateway from "shared/apis/RcGateway"
import OrderAdminGateway from "shared/apis/OrderAdminGateway"
import { convertLatLngsToCoordinates } from "shared/utils/HereMaps"

class FleetsStore {
  @observable fleets: Array<Object> = []
  @observable fleet: ?Object = null
  @observable installerFleets: Array<Object> = []
  @observable isLoadingFleets: boolean
  @observable isLoadingInstallerFleets: boolean
  @observable isLoadingFleet: boolean = false
  @observable fleetOptions: ?Object = null
  @observable fleetApplicationTypes: Array<string>
  @observable availableFleetProductConfig: Array<Object> = []
  @observable fleetProductConfigurations: Array<Object> = []
  @observable optionsSettings: Array<Object> = []
  @observable optionsForSelectedProduct: Array<Object> = []
  @observable isCreatingUser: boolean = false
  @observable isFetchingOptions: boolean = true
  @observable fleetCoverage: Array<Object> = []

  @action
  async getFleets() {
    runInAction(() => (this.isLoadingFleets = true))

    try {
      const result = await FleetGateway.fleets.getAllFleets()
      runInAction(
        () =>
          (this.fleets = (result && result.data && result.data.fleets) || [])
      )
    } catch (e) {
      throw e
    } finally {
      runInAction(() => (this.isLoadingFleets = false))
    }
  }

  @action
  setFleetCoverage(fleetCoverage: Array<Object>) {
    this.fleetCoverage = fleetCoverage
  }

  @action
  async getFleetOptions() {
    if (this.fleetOptions != null) return

    runInAction(() => (this.isLoadingFleets = true))

    try {
      const options = await FleetGateway.fleets.getFleetOptions()

      runInAction(() => {
        this.fleetOptions = options
      })
    } catch (e) {
      throw e
    } finally {
      runInAction(() => (this.isFetchingOptions = false))
    }
  }

  @action
  async updateFleetContact(id: string, email: string, phoneNumber: string) {
    this.isLoadingFleets = true

    try {
      await FleetGateway.fleets.updateFleetContact(id, { email, phoneNumber })
      await this.refreshCurrentFleet()
    } catch (e) {
      throw e
    } finally {
      this.isLoadingFleets = false
    }
  }

  async updateFleetCoverage(id: string) {
    if (this.fleetCoverage.length)
      try {
        await FleetGateway.fleets.updateFleetCoverage(id, {
          coverage: convertLatLngsToCoordinates(this.fleetCoverage)
        })
        runInAction(() => {
          if (this.fleet && this.fleet.coverage) {
            // $FlowIgnore
            this.fleet.coverage = convertLatLngsToCoordinates(
              this.fleetCoverage
            )
            this.fleetCoverage = []
          }
        })
        await this.refreshCurrentFleet()
      } catch (e) {
        throw e
      }
  }

  async updateFleetBdSystem(id: string, spokes: Array<string>) {
    try {
      const result = await FleetGateway.fleets.updateFleetBdSystem(id, {
        id: id,
        spokes: spokes
      })
      runInAction(() => {
        if (this.fleet && result.data && result.data.spokes) {
          this.fleet.spokes = result.data.spokes
        }
      })
    } catch (e) {
      throw e
    }
  }

  get countryOptions(): Array<string> {
    if (this.fleetOptions != null)
      return this.fleetOptions.countries.slice().sort()
    else return []
  }

  get regionOptions(): Array<string> {
    if (this.fleetOptions != null)
      return this.fleetOptions.fleetRegions.slice().sort()
    else return []
  }

  get ownershipOptions(): Array<string> {
    if (this.fleetOptions != null)
      return this.fleetOptions.ownerships.slice().sort()
    else return []
  }

  get fleetSystemOptions(): Array<string> {
    if (this.fleetOptions != null)
      return this.fleetOptions.fleetSystems.map((dict: Object) => {
        return [dict.fleetSystem].slice().sort()
      })
    else return []
  }

  get frogneFleetSystemInstanceOptions(): Array<string> {
    if (this.fleetOptions != null) {
      return this.fleetOptions.fleetSystems
        .filter(f => f.fleetSystem === "FROGNE")
        .flatMap(obj => {
          return obj.fleetSystemInstances.slice()
        })
    } else {
      return []
    }
  }

  get settlementPeriodOptions(): Array<string> {
    if (this.fleetOptions != null)
      return this.fleetOptions.settlementPeriods.slice().sort()
    else return []
  }

  get fleetApplicationTypesOptions(): Array<string> {
    if (this.fleetOptions != null) {
      return this.fleetOptions.fleetApplicationTypes.slice().sort()
    } else return []
  }

  get fleetSystemNamespaceOptions(): Array<string> {
    if (this.fleetOptions != null) {
      return this.fleetOptions.system_namespace.slice().sort()
    } else return []
  }

  @action
  async updateFleet(fleetId: string, values: Object) {
    try {
      await FleetGateway.fleets.updateFleet(fleetId, values)
      await this.getFleet(fleetId)
    } catch (e) {
      throw e
    } finally {
      runInAction(() => {
        this.isLoadingFleet = false
      })
    }
  }

  @action
  async addFleetService(id: string, values: Object) {
    this.isLoadingFleet = true

    try {
      const { serviceType } = values
      const obj = {}

      if (serviceType === "BOOKING_CHANNELS") {
        obj.brandIds = values.bookableChannels.map(id => parseInt(id))
      }

      await FleetGateway.fleets.addFleetService(id, serviceType, obj)

      await this.refreshCurrentFleet()
    } catch (e) {
      throw e
    } finally {
      runInAction(() => {
        this.isLoadingFleet = false
      })
    }
  }

  @action
  async deleteFleetService(id: string, serviceType: string) {
    try {
      await FleetGateway.fleets.deleteFleetService(id, serviceType)
      await this.refreshCurrentFleet()
    } catch (e) {
      throw e
    }
  }

  /**
   * This is using an endpoint in partner web that was
   * meant for reporting center installers. It will get
   * all fleets regardless of brand.
   */
  async getInstallerFleets() {
    runInAction(() => (this.isLoadingInstallerFleets = true))

    try {
      const result = await RcGateway.fleets.getAllFleets()

      runInAction(
        () =>
          (this.installerFleets =
            (result && result.data && result.data.fleetModels) || [])
      )
    } catch (e) {
      throw e
    } finally {
      runInAction(() => (this.isLoadingInstallerFleets = false))
    }
  }

  @action
  async getFleet(fleetId: string) {
    runInAction(() => {
      this.isLoadingFleet = true
      this.fleet = null
    })

    try {
      const fleet = await FleetGateway.fleets.getFleet(fleetId)
      runInAction(() => {
        this.fleet = fleet
      })
    } catch (e) {
      throw e
    } finally {
      runInAction(() => {
        this.isLoadingFleet = false
      })
    }
  }

  @action
  async createFleet(values: Object) {
    try {
      return await FleetGateway.fleets.createFleet(values)
    } catch (e) {
      throw e
    }
  }

  async refreshCurrentFleet() {
    if (this.fleet != null) {
      try {
        const fleet = await FleetGateway.fleets.getFleet(this.fleet.id)

        runInAction(() => {
          this.fleet = fleet
        })
      } catch (e) {
        throw e
      }
    }
  }

  async addUser(values: Object) {
    runInAction(() => (this.isCreatingUser = true))

    try {
      const id = this.fleet && this.fleet.id

      if (!id) throw new Error("Fleet is not specified in FleetStore")

      const data = await FleetGateway.fleets.addFleetUser(id, values)
      this.refreshCurrentFleet()

      return data
    } catch (e) {
      throw e
    } finally {
      runInAction(() => (this.isCreatingUser = false))
    }
  }

  async deleteUser(userId: number) {
    try {
      const fleetId = this.fleet && this.fleet.id

      if (!fleetId) throw new Error("Fleet is not specified in FleetStore")

      await FleetGateway.fleets.deleteFleetUser(fleetId, userId)
      this.refreshCurrentFleet()
    } catch (e) {
      throw e
    }
  }
  async getAllFleets() {
    try {
      const data = await OrderAdminGateway.orders.getAllFleets()
      runInAction(() => {
        this.fleets = (data && data.fleets) || []
      })
    } catch (e) {
      throw e
    }
  }
}

export default (new FleetsStore(): any)
